import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import { visuallyHidden } from "@mui/utils";
import dayjs from "dayjs";
import { ClioService, FilingReportItem, OpenAPI } from "../../../api";
import FilterableSelect from "../../../components/basic/FilterableSelect";
import { FILING_REPORT_STATUS_OPTIONS } from "./filingReportStatusOptions";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);

const STATUS_FILTER_ALL = "";
const STATUS_FILTER_NONE = "__none__";
const TASK_FILTER_ALL = "";
const COLUMN_COUNT = 9;

type SortableColumn =
  | "taskName"
  | "caseName"
  | "practiceArea"
  | "createdAt"
  | "dueDate"
  | "status"
  | "statusUpdatedAt"
  | "caseTeam";

type SortDirection = "asc" | "desc";

const HEAD_CELLS: { id: SortableColumn; label: string; minWidth?: number }[] = [
  { id: "caseName", label: "Case Name", minWidth: 200 },
  { id: "taskName", label: "Task", minWidth: 180 },
  { id: "practiceArea", label: "Practice Area", minWidth: 110 },
  { id: "createdAt", label: "Created At", minWidth: 130 },
  { id: "dueDate", label: "Due Date", minWidth: 100 },
  { id: "status", label: "Status", minWidth: 200 },
  { id: "statusUpdatedAt", label: "Status Updated", minWidth: 140 },
  { id: "caseTeam", label: "Case Team", minWidth: 180 },
];

const TABLE_HEADER_CELL_SX = {
  fontWeight: 600,
  fontSize: "0.8125rem",
  backgroundColor: "#6b7280",
  color: "#fff",
  borderBottom: "none",
  whiteSpace: "nowrap" as const,
  py: 1.25,
  "& .MuiTableSortLabel-root": { color: "rgba(255,255,255,0.85)" },
  "& .MuiTableSortLabel-root:hover": { color: "#fff" },
  "& .MuiTableSortLabel-root.Mui-active": { color: "#fff" },
  "& .MuiTableSortLabel-icon": { color: "#fff !important" },
};

const TABLE_BODY_CELL_SX = {
  fontSize: "0.8125rem",
  py: 1.25,
  borderColor: "divider",
  verticalAlign: "middle" as const,
};

function getSortValue(row: FilingReportItem, column: SortableColumn): string | number {
  switch (column) {
    case "taskName":
      return row.taskName?.toLowerCase() ?? "";
    case "caseName":
      return row.caseName?.toLowerCase() ?? "";
    case "practiceArea":
      return row.practiceArea?.toLowerCase() ?? "";
    case "caseTeam":
      return row.caseTeam?.toLowerCase() ?? "";
    case "status":
      return row.status?.toLowerCase() ?? "";
    case "createdAt":
      return row.createdAt ? dayjs(row.createdAt).valueOf() : 0;
    case "dueDate":
      return row.dueDate ? dayjs(row.dueDate).valueOf() : Number.MAX_SAFE_INTEGER;
    case "statusUpdatedAt":
      return row.statusUpdatedAt ? dayjs(row.statusUpdatedAt).valueOf() : 0;
    default:
      return "";
  }
}

function compareRows(
  a: FilingReportItem,
  b: FilingReportItem,
  orderBy: SortableColumn,
  order: SortDirection
) {
  const aValue = getSortValue(a, orderBy);
  const bValue = getSortValue(b, orderBy);

  if (aValue < bValue) {
    return order === "asc" ? -1 : 1;
  }
  if (aValue > bValue) {
    return order === "asc" ? 1 : -1;
  }
  return 0;
}

const ATTENTION_ROW_BG = "#fdecea";
const ATTENTION_ROW_HOVER_BG = "#f9d5d2";

function getRowCellBackground(needsAttention: boolean, isEven: boolean) {
  if (needsAttention) return ATTENTION_ROW_BG;
  return isEven ? "grey.50" : "background.paper";
}

function formatDateTime(value?: string | null) {
  if (!value) return "—";
  return dayjs(value).format("MM/DD/YYYY h:mm A");
}

function needsStatusAttention(row: FilingReportItem) {
  if (row.statusUpdatedAt) return false;
  if (!row.createdAt) return false;
  return dayjs().diff(dayjs(row.createdAt), "hour") >= 24;
}

export default function FilingReport() {
  const [reportData, setReportData] = useState<FilingReportItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL);
  const [taskFilter, setTaskFilter] = useState(TASK_FILTER_ALL);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [savingTaskId, setSavingTaskId] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<SortDirection>("desc");
  const [orderBy, setOrderBy] = useState<SortableColumn>("createdAt");

  const loadReport = useCallback(() => {
    setLoading(true);
    ClioService.getApiClioFilingReport()
      .then(setReportData)
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading filing report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const taskOptions = useMemo(
    () =>
      Array.from(
        new Set(reportData.map((row) => row.taskName).filter((name): name is string => !!name))
      )
        .sort((a, b) => a.localeCompare(b))
        .map((task) => ({ value: task, label: task })),
    [reportData]
  );

  const filteredData = useMemo(() => {
    let data = reportData;

    if (taskFilter) {
      data = data.filter((row) => row.taskName === taskFilter);
    }

    if (statusFilter === STATUS_FILTER_NONE) {
      data = data.filter((row) => !row.status);
    } else if (statusFilter) {
      data = data.filter((row) => row.status === statusFilter);
    }

    if (!searchTerm.trim()) {
      return data;
    }

    const lower = searchTerm.toLowerCase();
    return data.filter(
      (row) =>
        row.caseName?.toLowerCase().includes(lower) ||
        row.practiceArea?.toLowerCase().includes(lower) ||
        row.caseTeam?.toLowerCase().includes(lower) ||
        row.taskName?.toLowerCase().includes(lower) ||
        row.status?.toLowerCase().includes(lower)
    );
  }, [reportData, searchTerm, statusFilter, taskFilter]);

  const sortedData = useMemo(
    () => [...filteredData].sort((a, b) => compareRows(a, b, orderBy, order)),
    [filteredData, order, orderBy]
  );

  const paginatedData = sortedData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const uniqueCaseCount = useMemo(
    () => new Set(sortedData.map((row) => row.matterId)).size,
    [sortedData]
  );

  const handleRequestSort = (column: SortableColumn) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
    setPage(0);
  };

  const handleStatusChange = async (row: FilingReportItem, status: string) => {
    const nextStatus = status || null;
    const previousStatus = row.status ?? null;
    const previousUpdatedAt = row.statusUpdatedAt ?? null;
    const previousUpdatedByName = row.statusUpdatedByName ?? null;
    const updatedAt = new Date().toISOString();

    setReportData((prev) =>
      prev.map((item) =>
        item.taskId === row.taskId
          ? { ...item, status: nextStatus, statusUpdatedAt: updatedAt }
          : item
      )
    );

    setSavingTaskId(row.taskId);
    try {
      await ClioService.postApiClioSaveFilingReportStatuses([
        { taskId: row.taskId, matterId: row.matterId, status: nextStatus },
      ]);
    } catch {
      setReportData((prev) =>
        prev.map((item) =>
          item.taskId === row.taskId
            ? {
                ...item,
                status: previousStatus,
                statusUpdatedAt: previousUpdatedAt,
                statusUpdatedByName: previousUpdatedByName,
              }
            : item
        )
      );
      MySwal.fire({ icon: "error", title: <p>Error saving status.</p> });
    } finally {
      setSavingTaskId(null);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/filingReport/download`, {
        method: "POST",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          taskName: taskFilter || null,
          statusFilter: statusFilter || null,
          searchTerm: searchTerm.trim() || null,
        }),
      });

      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `FilingReport-${dayjs().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      MySwal.fire({ icon: "error", title: <p>Error downloading filing report.</p> });
    } finally {
      setDownloading(false);
    }
  };

  const isOverdue = (dueDate?: string | null) =>
    !!dueDate && dayjs(dueDate).isBefore(dayjs(), "day");

  return (
    <Box
      sx={{
        height: "calc(100vh - 120px)",
        display: "flex",
        flexDirection: "column",
        gap: 1.5,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          flexShrink: 0,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={600} lineHeight={1.3}>
            Filing Report
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Pending tasks assigned to Narissa Estrada
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={loading ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={loadReport}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={downloading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
            onClick={handleDownload}
            disabled={downloading || loading}
          >
            Download
          </Button>
        </Box>
      </Box>

      <Paper
        variant="outlined"
        sx={{
          px: 2,
          py: 1.5,
          flexShrink: 0,
          bgcolor: "grey.50",
        }}
      >
        <Box display="flex" flexWrap="wrap" gap={1.5} alignItems="center">
          <FilterableSelect
            label="Task"
            value={taskFilter || TASK_FILTER_ALL}
            options={taskOptions}
            onChange={(value) => {
              setTaskFilter(
                value === undefined || value === TASK_FILTER_ALL
                  ? TASK_FILTER_ALL
                  : String(value)
              );
              setPage(0);
            }}
            allOptionLabel="All tasks"
            allOptionValue={TASK_FILTER_ALL}
            searchPlaceholder="Filter tasks..."
            minWidth={220}
          />
          <FormControl size="small" sx={{ width: 200 }}>
            <InputLabel id="status-filter-label">Status</InputLabel>
            <Select
              labelId="status-filter-label"
              label="Status"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value={STATUS_FILTER_ALL}>All</MenuItem>
              <MenuItem value={STATUS_FILTER_NONE}>No status</MenuItem>
              {FILING_REPORT_STATUS_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            placeholder="Search..."
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(0);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 260 }, flexGrow: { sm: 1 }, maxWidth: 360 }}
          />
        </Box>
      </Paper>

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {!loading && sortedData.length > 0 && (
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ px: 2, py: 1, display: "block" }}
            >
              {uniqueCaseCount} case{uniqueCaseCount === 1 ? "" : "s"} · {sortedData.length} task
              {sortedData.length === 1 ? "" : "s"}
            </Typography>
            <Divider />
          </>
        )}

        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...TABLE_HEADER_CELL_SX, width: 44, minWidth: 44 }}>
                  #
                </TableCell>
                {HEAD_CELLS.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                    sx={{ ...TABLE_HEADER_CELL_SX, minWidth: headCell.minWidth }}
                  >
                    <TableSortLabel
                      active={orderBy === headCell.id}
                      direction={orderBy === headCell.id ? order : "asc"}
                      onClick={() => handleRequestSort(headCell.id)}
                    >
                      {headCell.label}
                      {orderBy === headCell.id ? (
                        <Box component="span" sx={visuallyHidden}>
                          {order === "desc" ? "sorted descending" : "sorted ascending"}
                        </Box>
                      ) : null}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} align="center" sx={{ py: 8 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={COLUMN_COUNT}
                    align="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    No tasks match the selected filters.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedData.map((row, index) => {
                  const needsAttention = needsStatusAttention(row);
                  const rowNumber = page * rowsPerPage + index + 1;
                  const cellBackground = getRowCellBackground(needsAttention, index % 2 === 1);
                  const cellSx = { ...TABLE_BODY_CELL_SX, bgcolor: cellBackground };

                  return (
                    <TableRow
                      key={row.taskId}
                      hover
                      sx={{
                        "&:hover .MuiTableCell-root": {
                          bgcolor: needsAttention ? ATTENTION_ROW_HOVER_BG : "action.hover",
                        },
                        "&:last-child td": { borderBottom: 0 },
                      }}
                    >
                      <TableCell
                        sx={{
                          ...cellSx,
                          color: "text.secondary",
                          fontWeight: 500,
                          width: 44,
                        }}
                      >
                        {rowNumber}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, fontWeight: 500, maxWidth: 240 }}>
                        {row.caseName}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, maxWidth: 200 }}>{row.taskName}</TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                        {row.practiceArea ?? "—"}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                        {formatDateTime(row.createdAt)}
                      </TableCell>
                      <TableCell
                        sx={{
                          ...cellSx,
                          whiteSpace: "nowrap",
                          color: isOverdue(row.dueDate) ? "error.main" : "inherit",
                          fontWeight: isOverdue(row.dueDate) ? 600 : 400,
                        }}
                      >
                        {row.dueDate ? dayjs(row.dueDate).format("MM/DD/YYYY") : "—"}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, minWidth: 200 }}>
                        <FormControl
                          size="small"
                          fullWidth
                          disabled={savingTaskId === row.taskId}
                        >
                          <Select
                            displayEmpty
                            value={row.status ?? ""}
                            onChange={(e) => handleStatusChange(row, e.target.value)}
                            renderValue={(selected) =>
                              selected ? (
                                selected
                              ) : (
                                <Typography color="text.secondary" variant="body2">
                                  Select status
                                </Typography>
                              )
                            }
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {FILING_REPORT_STATUS_OPTIONS.map((option) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                        {formatDateTime(row.statusUpdatedAt)}
                      </TableCell>
                      <TableCell sx={{ ...cellSx, whiteSpace: "pre-line" }}>
                        {row.caseTeam}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider />
        <TablePagination
          rowsPerPageOptions={[10, 25, 50, 100]}
          component="div"
          count={sortedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          sx={{ flexShrink: 0, minHeight: 52 }}
        />
      </Paper>
    </Box>
  );
}
